import React, { useState } from "react";
import { Input, DatePicker, Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";
import { getViewableFileUrl } from "../../../../../utils/convertS3ToCloudFront";
import dayjs from "dayjs";

function trimUpperOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim().toUpperCase();
  return s === "" ? null : s;
}

function trimOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

const InvoiceBillingForm = ({
  invoiceData,
  tab,
  onSaveBillingInfo,
  justView = false,
}) => {
  const [brandEdit, setBrandEdit] = useState(false);
  const [brandDraft, setBrandDraft] = useState("");
  const [campaignEdit, setCampaignEdit] = useState(false);
  const [campaignDraft, setCampaignDraft] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [invoiceRemarkEdit, setInvoiceRemarkEdit] = useState(false);
  const [invoiceRemarkDraft, setInvoiceRemarkDraft] = useState("");
  const [savingInvoiceRemark, setSavingInvoiceRemark] = useState(false);
  const [subRemarkEdit, setSubRemarkEdit] = useState(false);
  const [subRemarkDraft, setSubRemarkDraft] = useState("");
  const [savingSubRemark, setSavingSubRemark] = useState(false);
  const [refNoEdit, setRefNoEdit] = useState(false);
  const [refNoDraft, setRefNoDraft] = useState("");
  const [savingRefNo, setSavingRefNo] = useState(false);
  const [refDateEdit, setRefDateEdit] = useState(false);
  const [refDateDraft, setRefDateDraft] = useState(null);
  const [savingRefDate, setSavingRefDate] = useState(false);

  const canEditBillingInfo =
    Boolean(invoiceData?.billingId) && !justView && onSaveBillingInfo;

  const startBrandEdit = () => {
    setBrandDraft(
      invoiceData?.brandName != null && invoiceData.brandName !== ""
        ? String(invoiceData.brandName).trim().toUpperCase()
        : ""
    );
    setBrandEdit(true);
  };

  const startCampaignEdit = () => {
    setCampaignDraft(
      invoiceData?.campaignName != null && invoiceData.campaignName !== ""
        ? String(invoiceData.campaignName).trim().toUpperCase()
        : ""
    );
    setCampaignEdit(true);
  };

  const startRefNoEdit = () => {
    setRefNoDraft(
      invoiceData?.refNo != null && invoiceData.refNo !== ""
        ? String(invoiceData.refNo).trim()
        : ""
    );
    setRefNoEdit(true);
  };

  const startRefDateEdit = () => {
    setRefDateDraft(invoiceData?.refDate ? dayjs(invoiceData.refDate) : null);
    setRefDateEdit(true);
  };

  const handleSaveBrand = async () => {
    if (justView || !onSaveBillingInfo) return;
    const next = trimUpperOrNull(brandDraft);
    const prev = trimUpperOrNull(invoiceData?.brandName);
    if (next === prev) {
      setBrandEdit(false);
      return;
    }
    setSavingBrand(true);
    try {
      await onSaveBillingInfo({ brandName: next });
      setBrandEdit(false);
    } catch {
      /* parent notified */
    } finally {
      setSavingBrand(false);
    }
  };

  const handleSaveCampaign = async () => {
    if (justView || !onSaveBillingInfo) return;
    const next = trimUpperOrNull(campaignDraft);
    const prev = trimUpperOrNull(invoiceData?.campaignName);
    if (next === prev) {
      setCampaignEdit(false);
      return;
    }
    setSavingCampaign(true);
    try {
      await onSaveBillingInfo({ campaignName: next });
      setCampaignEdit(false);
    } catch {
      /* parent notified */
    } finally {
      setSavingCampaign(false);
    }
  };

  const handleSaveRefNo = async () => {
    if (justView || !onSaveBillingInfo) return;
    const next = trimOrNull(refNoDraft);
    const prev = trimOrNull(invoiceData?.refNo);
    if (next === prev) {
      setRefNoEdit(false);
      return;
    }
    setSavingRefNo(true);
    try {
      await onSaveBillingInfo({ refNo: next });
      setRefNoEdit(false);
    } catch {
      /* parent notified */
    } finally {
      setSavingRefNo(false);
    }
  };

  const handleSaveRefDate = async () => {
    if (justView || !onSaveBillingInfo) return;
    const next = refDateDraft ? refDateDraft.toISOString() : null;
    const prev = invoiceData?.refDate ? dayjs(invoiceData.refDate).toISOString() : null;
    if (next === prev) {
      setRefDateEdit(false);
      return;
    }
    setSavingRefDate(true);
    try {
      await onSaveBillingInfo({ refDate: next });
      setRefDateEdit(false);
    } catch {
      /* parent notified */
    } finally {
      setSavingRefDate(false);
    }
  };

  const startInvoiceRemarkEdit = () => {
    setInvoiceRemarkDraft(
      invoiceData?.invoiceRemark != null
        ? String(invoiceData.invoiceRemark).trim()
        : ""
    );
    setInvoiceRemarkEdit(true);
  };

  const handleSaveInvoiceRemark = async () => {
    if (justView || !onSaveBillingInfo) return;
    const next = trimOrNull(invoiceRemarkDraft);
    const prev = trimOrNull(invoiceData?.invoiceRemark);
    if (next === prev) {
      setInvoiceRemarkEdit(false);
      return;
    }
    setSavingInvoiceRemark(true);
    try {
      await onSaveBillingInfo({ remark: next });
      setInvoiceRemarkEdit(false);
    } catch {
      /* parent notified */
    } finally {
      setSavingInvoiceRemark(false);
    }
  };

  const startSubRemarkEdit = () => {
    setSubRemarkDraft(
      invoiceData?.invoiceSubRemark != null
        ? String(invoiceData.invoiceSubRemark).trim()
        : ""
    );
    setSubRemarkEdit(true);
  };

  const handleSaveSubRemark = async () => {
    if (justView || !onSaveBillingInfo) return;
    const next = trimOrNull(subRemarkDraft);
    const prev = trimOrNull(invoiceData?.invoiceSubRemark);
    if (next === prev) {
      setSubRemarkEdit(false);
      return;
    }
    setSavingSubRemark(true);
    try {
      await onSaveBillingInfo({ subRemark: next });
      setSubRemarkEdit(false);
    } catch {
      /* parent notified */
    } finally {
      setSavingSubRemark(false);
    }
  };

  const invoiceRemarkTrimmed =
    invoiceData?.invoiceRemark && String(invoiceData.invoiceRemark).trim();
  const invoiceSubRemarkTrimmed =
    invoiceData?.invoiceSubRemark &&
    String(invoiceData.invoiceSubRemark).trim();

  // console.log("invoiceData", invoiceData);
  // console.log("tab is: ", tab);

  return (
    <div className="sales-order-form">
      <div className="sales-order-field">
        <div className="sales-order-field-name">
          {tab === "3" ? "Bill Number" : "Sales Order ID"}
        </div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={
              tab === "3"
                ? invoiceData?.billNumber || "NA"
                : invoiceData?.stamp || "NA"
            }
            readOnly
          />
        </div>
      </div>

      {/* 
      <div className="sales-order-field">
        <div className="sales-order-field-name">R.O. No.</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={invoiceData?.roNumber}
            readOnly
          />
        </div>
      </div> */}

      <div className="sales-order-field">
        <div className="sales-order-field-name">Party Name</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={
              invoiceData?.splitParentCompanyName ||
              invoiceData?.parentCompanyName
            }
            readOnly
          />
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">BDF Remark</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={invoiceData?.remark}
            readOnly
          />
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">GST No.</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={invoiceData?.splitGSTNumber || invoiceData?.gstNumber}
            readOnly
          />
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">Brand Name</div>
        <div
          className="sales-order-input-parent"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Input
            className="custom-input-bdf"
            value={
              brandEdit
                ? brandDraft
                : invoiceData?.brandName
                  ? String(invoiceData.brandName).toUpperCase()
                  : ""
            }
            onChange={(e) => {
              if (brandEdit) setBrandDraft(e.target.value.toUpperCase());
            }}
            readOnly={!brandEdit || justView}
            placeholder="Brand name"
            onPressEnter={() => {
              if (brandEdit && !justView) handleSaveBrand();
            }}
            style={{ flex: 1, minWidth: 160 }}
          />
          {!justView && canEditBillingInfo && !brandEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Edit brand name"
              onClick={startBrandEdit}
            />
          )}
          {!justView && brandEdit && (
            <>
              <Button
                type="primary"
                size="small"
                loading={savingBrand}
                onClick={handleSaveBrand}
              >
                Save
              </Button>
              <Button
                size="small"
                disabled={savingBrand}
                onClick={() => setBrandEdit(false)}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">Campaign Name</div>
        <div
          className="sales-order-input-parent"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Input
            className="custom-input-bdf"
            value={
              campaignEdit
                ? campaignDraft
                : invoiceData?.campaignName
                  ? String(invoiceData.campaignName).toUpperCase()
                  : ""
            }
            onChange={(e) => {
              if (campaignEdit)
                setCampaignDraft(e.target.value.toUpperCase());
            }}
            readOnly={!campaignEdit || justView}
            placeholder="Campaign name"
            onPressEnter={() => {
              if (campaignEdit && !justView) handleSaveCampaign();
            }}
            style={{ flex: 1, minWidth: 160 }}
          />
          {!justView && canEditBillingInfo && !campaignEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Edit campaign name"
              onClick={startCampaignEdit}
            />
          )}
          {!justView && campaignEdit && (
            <>
              <Button
                type="primary"
                size="small"
                loading={savingCampaign}
                onClick={handleSaveCampaign}
              >
                Save
              </Button>
              <Button
                size="small"
                disabled={savingCampaign}
                onClick={() => setCampaignEdit(false)}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">Campaign Duration (Days)</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={invoiceData?.campaignDuration}
            readOnly
          />
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">Sales Person Name</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={invoiceData?.salesPerson}
            readOnly
          />
        </div>
      </div>
      <div className="sales-order-field">
        <div className="sales-order-field-name">BDF Serial No.</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={invoiceData?.bdfStamp}
            readOnly
          />
        </div>
      </div>
      {tab === "3" && (
        <>
          <div className="sales-order-field">
            <div className="sales-order-field-name">Created By</div>
            <div className="sales-order-input-parent">
              <Input
                className="custom-input-bdf"
                value={invoiceData?.createdBy}
                readOnly
              />
            </div>
          </div>
          <div className="sales-order-field">
            <div className="sales-order-field-name">Alias Party Name</div>
            <div className="sales-order-input-parent">
              <Input
                className="custom-input-bdf"
                value={invoiceData?.aliasPartyName}
                readOnly
              />
            </div>
          </div>
          <div className="sales-order-field">
            <div className="sales-order-field-name">HSN Code</div>
            <div className="sales-order-input-parent">
              <Input
                className="custom-input-bdf"
                value={invoiceData?.HSN}
                readOnly
              />
            </div>
          </div>

          <div className="sales-order-field">
            <div className="sales-order-field-name">Ref No.</div>
            <div
              className="sales-order-input-parent"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Input
                className="custom-input-bdf"
                value={refNoEdit ? refNoDraft : invoiceData?.refNo || ""}
                onChange={(e) => {
                  if (refNoEdit) setRefNoDraft(e.target.value);
                }}
                readOnly={!refNoEdit || justView}
                placeholder="Ref no."
                onPressEnter={() => {
                  if (refNoEdit && !justView) handleSaveRefNo();
                }}
                style={{ flex: 1, minWidth: 160 }}
              />
              {!justView && canEditBillingInfo && !refNoEdit && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  aria-label="Edit ref no"
                  onClick={startRefNoEdit}
                />
              )}
              {!justView && refNoEdit && (
                <>
                  <Button
                    type="primary"
                    size="small"
                    loading={savingRefNo}
                    onClick={handleSaveRefNo}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    disabled={savingRefNo}
                    onClick={() => setRefNoEdit(false)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="sales-order-field">
            <div className="sales-order-field-name">Ref Date</div>
            <div
              className="sales-order-input-parent"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <DatePicker
                className="custom-input-bdf"
                value={
                  refDateEdit
                    ? refDateDraft
                    : invoiceData?.refDate
                      ? dayjs(invoiceData.refDate)
                      : null
                }
                onChange={(date) => {
                  if (refDateEdit) setRefDateDraft(date);
                }}
                format="DD/MM/YYYY"
                disabled={!refDateEdit || justView}
                style={{ flex: 1, minWidth: 160 }}
              />
              {!justView && canEditBillingInfo && !refDateEdit && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  aria-label="Edit ref date"
                  onClick={startRefDateEdit}
                />
              )}
              {!justView && refDateEdit && (
                <>
                  <Button
                    type="primary"
                    size="small"
                    loading={savingRefDate}
                    onClick={handleSaveRefDate}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    disabled={savingRefDate}
                    onClick={() => setRefDateEdit(false)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {invoiceData?.roFile && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">RO File</div>
          <div className="sales-order-input-parent">
            <a
              href={getViewableFileUrl(invoiceData?.roFile)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1890ff",
                textDecoration: "underline",
                wordBreak: "break-all",
              }}
            >
              View RO File
            </a>
          </div>
        </div>
      )}

      {invoiceData?.splitAmount && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">Total Splitted Amount</div>
          <div className="sales-order-input-parent">
            <Input
              className="custom-input-bdf"
              value={formatIndianNumber(invoiceData?.splitAmount)}
              readOnly
            />
          </div>
        </div>
      )}

      {(canEditBillingInfo || invoiceRemarkTrimmed) && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">Invoice Remark</div>
          <div
            className="sales-order-input-parent"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Input.TextArea
              className="custom-input-bdf"
              rows={3}
              value={
                invoiceRemarkEdit
                  ? invoiceRemarkDraft
                  : invoiceRemarkTrimmed || ""
              }
              onChange={(e) => {
                if (invoiceRemarkEdit) setInvoiceRemarkDraft(e.target.value);
              }}
              readOnly={!invoiceRemarkEdit || justView}
              placeholder="Invoice remark"
              style={{ flex: 1, minWidth: 160 }}
            />
            {!justView && canEditBillingInfo && !invoiceRemarkEdit && (
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label="Edit invoice remark"
                onClick={startInvoiceRemarkEdit}
              />
            )}
            {!justView && invoiceRemarkEdit && (
              <>
                <Button
                  type="primary"
                  size="small"
                  loading={savingInvoiceRemark}
                  onClick={handleSaveInvoiceRemark}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  disabled={savingInvoiceRemark}
                  onClick={() => setInvoiceRemarkEdit(false)}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {(canEditBillingInfo || invoiceSubRemarkTrimmed) && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">Sub Remark</div>
          <div
            className="sales-order-input-parent"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Input.TextArea
              className="custom-input-bdf"
              rows={2}
              value={
                subRemarkEdit ? subRemarkDraft : invoiceSubRemarkTrimmed || ""
              }
              onChange={(e) => {
                if (subRemarkEdit) setSubRemarkDraft(e.target.value);
              }}
              readOnly={!subRemarkEdit || justView}
              placeholder="Sub remark"
              style={{ flex: 1, minWidth: 160 }}
            />
            {!justView && canEditBillingInfo && !subRemarkEdit && (
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label="Edit sub remark"
                onClick={startSubRemarkEdit}
              />
            )}
            {!justView && subRemarkEdit && (
              <>
                <Button
                  type="primary"
                  size="small"
                  loading={savingSubRemark}
                  onClick={handleSaveSubRemark}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  disabled={savingSubRemark}
                  onClick={() => setSubRemarkEdit(false)}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceBillingForm;
